import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Users.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const DepartmentsList = forwardRef(({ onSelectDepartment, selectedDepartmentId, onNew }, ref) => {
  const { t } = useTranslation('admin');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchDepartments
  }));

  useEffect(() => {
    fetchDepartments();
  }, [statusFilter]);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/departments/');
      if (statusFilter !== 'all') {
        url += `?is_active=${statusFilter === 'active'}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      } else {
        setError(t('departments.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError(t('departments.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredDepartments = departments.filter(dept => {
    const matchesSearch = (
      dept.department_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.department_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dept.description && dept.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return matchesSearch;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('departments.filters.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('departments.filters.status.inactive')}</span>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && departments.length === 0) {
    return <div className="list-container"><div className="loading">{t('departments.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('departments.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('departments.newDepartment')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('departments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('departments.filters.allStatus')}</option>
            <option value="active">{t('departments.filters.status.active')}</option>
            <option value="inactive">{t('departments.filters.status.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('departments.tableHeaders.code')}</th>
              <th>{t('departments.tableHeaders.name')}</th>
              <th>{t('departments.tableHeaders.description')}</th>
              <th>{t('departments.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredDepartments.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  {searchTerm ? t('departments.noData.searching') : t('departments.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredDepartments.map(dept => (
                <tr
                  key={dept.department_id}
                  onClick={() => onSelectDepartment(dept.department_id)}
                  className={selectedDepartmentId === dept.department_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">{dept.department_code}</span>
                  </td>
                  <td>
                    <strong>{dept.department_name}</strong>
                  </td>
                  <td>
                    <span className="dept-description">{dept.description || '-'}</span>
                  </td>
                  <td>
                    {getStatusBadge(dept.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default DepartmentsList;